import { useCallback } from 'react';

import useGuideMessaging from './useGuideMessaging';
import useSendbird from './useSendbird';

/**
 * Sends a user message on the member's guide messaging channel via the
 * connected Sendbird Chat SDK.
 */
export const useSendGuideMessage = () => {
    const { guideMessaging } = useGuideMessaging();
    const { sdk: sendbirdSdk } = useSendbird();

    const sendGuideMessage = useCallback(
        async (message: string): Promise<void> => {
            if (sendbirdSdk == null) {
                throw new Error('Sendbird is not connected');
            }

            const channelUrl =
                guideMessaging?.currentChat.channel.channelUrl ?? null;
            if (channelUrl == null) {
                throw new Error('Guide messaging channel is unavailable');
            }

            const channel =
                await sendbirdSdk.groupChannel.getChannel(channelUrl);

            if (channel == null) {
                throw new Error('Guide messaging channel could not be found');
            }

            await new Promise<void>((resolve, reject) => {
                channel
                    .sendUserMessage({ message })
                    .onSucceeded(() => {
                        resolve();
                    })
                    .onFailed((error) => {
                        reject(error);
                    });
            });
        },
        [guideMessaging, sendbirdSdk],
    );

    return {
        sendGuideMessage,
        canSend:
            sendbirdSdk != null &&
            guideMessaging?.currentChat.channel.channelUrl != null,
    };
};
